'use client';

import { c as _c } from "react/compiler-runtime";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@achra/ui/accordion';
import { Card } from '@achra/ui/card';
import { cn } from '@achra/ui/lib/utils';
import { Check } from 'lucide-react';
import { type ComponentProps, createContext, useContext } from 'react';
import { formatSummaryPrice, isIncludedValue } from '@/modules/service-purchase/lib/utils';
import type { PurchaseOptionGroup } from '@/modules/service-purchase/types';
interface GroupPriceEntry {
  amount: number;
  originalAmount: number;
  discountPercent: number | null;
}
interface SummaryContextValue {
  sectionLabel: string;
  totalSuffix: string;
  isRecurring: boolean;
  headerLabel?: string;
  groupPrices?: Map<string, GroupPriceEntry>;
  isCustomPricing?: boolean;
  currency?: string | null;
}
const SummaryContext = createContext<SummaryContextValue | null>(null);
function useSummary() {
  const context = useContext(SummaryContext);
  if (!context) {
    throw new Error("Summary subcomponents must be used within Summary.Provider");
  }
  return context;
}
interface SummaryProviderProps {
  children: React.ReactNode;
  sectionLabel: string;
  totalSuffix: string;
  headerLabel?: string;
  groupPrices?: Map<string, GroupPriceEntry>;
  isCustomPricing?: boolean;
  currency?: string | null;
}
function SummaryProvider(t0) {
  const $ = _c(11);
  const {
    children,
    sectionLabel,
    totalSuffix,
    headerLabel: t1,
    groupPrices,
    isCustomPricing,
    currency
  } = t0;
  const headerLabel = t1 === undefined ? "Pricing Summary" : t1;
  const isRecurring = sectionLabel === "Recurring";
  let t2;
  if ($[0] !== currency || $[1] !== groupPrices || $[2] !== headerLabel || $[3] !== isCustomPricing || $[4] !== isRecurring || $[5] !== sectionLabel || $[6] !== totalSuffix) {
    t2 = {
      sectionLabel,
      totalSuffix,
      isRecurring,
      headerLabel,
      groupPrices,
      isCustomPricing,
      currency
    };
    $[0] = currency;
    $[1] = groupPrices;
    $[2] = headerLabel;
    $[3] = isCustomPricing;
    $[4] = isRecurring;
    $[5] = sectionLabel;
    $[6] = totalSuffix;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const value = t2;
  let t3;
  if ($[8] !== children || $[9] !== value) {
    t3 = <SummaryContext.Provider value={value}>{children}</SummaryContext.Provider>;
    $[8] = children;
    $[9] = value;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}
function SummaryCard(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Card className="gap-0 overflow-hidden p-0" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function SummaryContent(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Accordion type="multiple"><div className="flex flex-col gap-0 px-4 py-3 lg:px-6" {...props} /></Accordion>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
interface SummaryRowProps {
  label: string;
  value: string | null;
}
function SummaryRow(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-sm/5.5 lg:text-base/6">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = isIncludedValue(value) ? <Check className="text-status-success size-4 shrink-0" aria-hidden={true} /> : <span className="text-foreground text-sm/5.5 lg:text-base/6">{value}</span>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="border-border text-foreground/70 ml-10 flex items-center justify-between border-b py-2 pl-0" data-slot="summary-section-row">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
interface SummaryGroupProps {
  group: PurchaseOptionGroup;
}
function SummaryGroup(t0) {
  const $ = _c(28);
  const {
    group
  } = t0;
  const {
    isRecurring,
    groupPrices,
    isCustomPricing,
    currency,
    totalSuffix
  } = useSummary();
  let t1;
  if ($[0] !== currency || $[1] !== group.id || $[2] !== group.resolvedPrice || $[3] !== groupPrices || $[4] !== isCustomPricing || $[5] !== isRecurring || $[6] !== totalSuffix) {
    const priceEntry = groupPrices?.get(group.id);
    const displayAmount = priceEntry?.amount ?? group.resolvedPrice;
    t1 = isCustomPricing && displayAmount === 0 ? "" : formatSummaryPrice({
      amount: displayAmount,
      isRecurring,
      suffix: totalSuffix,
      currency
    });
    $[0] = currency;
    $[1] = group.id;
    $[2] = group.resolvedPrice;
    $[3] = groupPrices;
    $[4] = isCustomPricing;
    $[5] = isRecurring;
    $[6] = totalSuffix;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const formattedPrice = t1;
  const expandable = group.services.length > 0;
  const t2 = group.id;
  const t3 = !expandable && "pointer-events-none cursor-default [&>svg]:invisible";
  let t4;
  if ($[8] !== t3) {
    t4 = cn("flex-row-reverse items-center justify-end gap-2 py-3 hover:no-underline [&>svg]:shrink-0 [&>svg]:translate-y-0 [&[data-state=open]>svg]:rotate-180", t3);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `${group.name}, ${formattedPrice}`;
  let t6;
  if ($[10] !== group.name) {
    t6 = <span className="text-foreground text-sm/5.5 lg:text-base/6">{group.name}</span>;
    $[10] = group.name;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== formattedPrice) {
    t7 = <span className="text-foreground text-sm/5.5 font-semibold tabular-nums lg:text-base/6">{formattedPrice}</span>;
    $[12] = formattedPrice;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex flex-1 items-center justify-between gap-4">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t4 || $[18] !== t5 || $[19] !== t8) {
    t9 = <AccordionTrigger className={t4} aria-label={t5}>{t8}</AccordionTrigger>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== expandable || $[22] !== group.services) {
    t10 = expandable && <AccordionContent><div className="flex flex-col gap-0">{group.services.flatMap(_temp)}</div></AccordionContent>;
    $[21] = expandable;
    $[22] = group.services;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== group.id || $[25] !== t10 || $[26] !== t9) {
    t11 = <AccordionItem value={t2} className="border-border border-b last:border-b-0">{t9}{t10}</AccordionItem>;
    $[24] = group.id;
    $[25] = t10;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp(service) {
  return [...(service.resolvedValue != null ? [<SummaryRow key={service.id} label={service.title} value={service.resolvedValue} />] : service.metrics.length === 0 ? [<SummaryRow key={service.id} label={service.title} value="Included" />] : []), ...service.metrics.map(m => <SummaryRow key={`${service.id}-${m.label}`} label={m.label} value={m.value} />)];
}
function SummaryCardHeader() {
  const $ = _c(7);
  const {
    sectionLabel,
    headerLabel
  } = useSummary();
  let t0;
  if ($[0] !== headerLabel) {
    t0 = <span className="text-foreground text-sm tracking-wide lg:text-base/6">{headerLabel}</span>;
    $[0] = headerLabel;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== sectionLabel) {
    t1 = <span className="text-foreground/70 text-sm/5.5 font-semibold tracking-wider">{sectionLabel}</span>;
    $[2] = sectionLabel;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t0 || $[5] !== t1) {
    t2 = <header className="bg-accent flex items-center justify-between px-4 py-2 font-semibold uppercase lg:px-6" data-slot="summary-section-header">{t0}{t1}</header>;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
interface SummaryFooterProps {
  totalAmount: number;
  originalTotalAmount?: number;
}
function SummaryTotal(t0) {
  const $ = _c(31);
  const {
    totalAmount,
    originalTotalAmount
  } = t0;
  const {
    sectionLabel,
    totalSuffix,
    isRecurring,
    isCustomPricing,
    currency
  } = useSummary();
  let t1;
  if ($[0] !== currency || $[1] !== isCustomPricing || $[2] !== isRecurring || $[3] !== totalAmount || $[4] !== totalSuffix) {
    t1 = formatSummaryPrice({
      amount: totalAmount,
      isRecurring,
      suffix: totalSuffix,
      isCustomPricing,
      currency
    });
    $[0] = currency;
    $[1] = isCustomPricing;
    $[2] = isRecurring;
    $[3] = totalAmount;
    $[4] = totalSuffix;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const formattedTotal = t1;
  const hasDiscount = originalTotalAmount != null && originalTotalAmount > totalAmount;
  let t2;
  if ($[6] !== currency || $[7] !== hasDiscount || $[8] !== isRecurring || $[9] !== originalTotalAmount || $[10] !== totalSuffix) {
    t2 = hasDiscount ? formatSummaryPrice({
      amount: originalTotalAmount,
      isRecurring,
      suffix: totalSuffix,
      currency
    }) : null;
    $[6] = currency;
    $[7] = hasDiscount;
    $[8] = isRecurring;
    $[9] = originalTotalAmount;
    $[10] = totalSuffix;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const formattedOriginalTotal = t2;
  let t3;
  if ($[12] !== hasDiscount || $[13] !== originalTotalAmount || $[14] !== totalAmount) {
    t3 = hasDiscount ? Math.round((originalTotalAmount - totalAmount) / originalTotalAmount * 100) : null;
    $[12] = hasDiscount;
    $[13] = originalTotalAmount;
    $[14] = totalAmount;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const discountPercent = t3;
  let t4;
  if ($[16] !== sectionLabel) {
    t4 = sectionLabel.toLowerCase();
    $[16] = sectionLabel;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t4) {
    t5 = <span className="text-foreground/70 text-sm/5.5 font-semibold tracking-wide uppercase">Total {t4}</span>;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== discountPercent || $[21] !== formattedOriginalTotal) {
    t6 = formattedOriginalTotal && <span className="flex items-center gap-1.5"><span className="text-foreground/50 text-xs font-medium tabular-nums line-through lg:text-sm/5.5">{formattedOriginalTotal}</span>{discountPercent != null && <span className="bg-primary/10 text-primary rounded-full px-1.5 py-0.5 text-[10px] leading-none font-semibold lg:text-xs">-{discountPercent}%</span>}</span>;
    $[20] = discountPercent;
    $[21] = formattedOriginalTotal;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== formattedTotal) {
    t7 = <span className="text-primary text-sm font-semibold tabular-nums lg:text-base/6">{formattedTotal}</span>;
    $[23] = formattedTotal;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t6 || $[26] !== t7) {
    t8 = <div className="flex flex-col items-end">{t6}{t7}</div>;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t5 || $[29] !== t8) {
    t9 = <footer className="bg-accent flex items-center justify-between px-4 py-2 lg:px-6" data-slot="summary-section-footer">{t5}{t8}</footer>;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
}
const Summary = {
  Provider: SummaryProvider,
  Card: SummaryCard,
  Content: SummaryContent,
  Header: SummaryCardHeader,
  Row: SummaryRow,
  Group: SummaryGroup,
  Total: SummaryTotal
};
export { Summary };